import { NETWORK_CHART_METRICS } from '../consts';

export function formatValue(value, type, currencySymbol) {
    if (value === null || value === undefined || value === 'N/A') return 'N/A';
    switch (type) {
        case 'number':
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
        case 'percentage':
            return `${(Number(value) * 100).toFixed(1)}%`;
        case 'currency':
            return `${currencySymbol || '$'}${Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            })}`;
        default:
            return String(value);
    }
}

// Transforms (network × date) API rows into the chartData shape LineChart expects.
export function buildFraudChartData(results) {
    if (!results || !results.length) return null;

    const dimensions = {};
    /** @type {Record<string, Record<string, Record<string, number>>>} */
    const metricBuckets = {};
    NETWORK_CHART_METRICS.forEach(m => {
        metricBuckets[m.name] = {};
    });

    let hasDateData = false;
    results.forEach(row => {
        const source = row.source || 'Unknown';
        dimensions[source] = source;

        let dateKey;
        if (row.date_field) {
            dateKey = row.date_field;
        } else if (row.start_date && row.end_date && row.start_date !== row.end_date) {
            // Weekly / monthly: produce the "start ... end" format LineChart expects
            dateKey = `${row.start_date} ... ${row.end_date}`;
        } else {
            dateKey = row.start_date || '';
        }

        if (!dateKey) return;
        hasDateData = true;

        NETWORK_CHART_METRICS.forEach(m => {
            if (!metricBuckets[m.name][dateKey]) metricBuckets[m.name][dateKey] = {};
            if (row[m.name] !== undefined && row[m.name] !== null) {
                metricBuckets[m.name][dateKey][source] = row[m.name];
            }
        });
    });

    if (!Object.keys(dimensions).length || !hasDateData) return null;

    // Rank networks by total fraud volume (sum of absolute metric values across all dates
    // and metrics), descending. The dashboard's contract is to surface the highest-volume
    // networks first; this ordering is what defines "top networks" for the chart.
    const dimensionTotals = {};
    Object.keys(dimensions).forEach(dim => {
        dimensionTotals[dim] = 0;
    });
    NETWORK_CHART_METRICS.forEach(m => {
        Object.values(metricBuckets[m.name]).forEach(dateSlice => {
            Object.entries(dateSlice).forEach(([dim, val]) => {
                if (val !== null && val !== undefined) {
                    dimensionTotals[dim] = (dimensionTotals[dim] || 0) + Math.abs(Number(val));
                }
            });
        });
    });
    const dimensionsSorted = Object.keys(dimensions).sort(
        (a, b) => (dimensionTotals[b] || 0) - (dimensionTotals[a] || 0)
    );

    return {
        dimensions,
        dimensionsSorted,
        metrics: NETWORK_CHART_METRICS.map((m, i) => ({ id: i, ...m })),
        ...metricBuckets,
        orgOptions: { metricsLimit: 3 },
    };
}

// Builds the ag-Grid pinned "Total" row: dimension columns show the total label, metric
// columns show their total value (or null when absent from the totals payload).
export function buildPinnedTopRow(columns, totals, totalLabel) {
    const row = {};
    columns.forEach(col => {
        row[col.key] = col.type === 'dimension' ? totalLabel : totals[col.key] ?? null;
    });
    return row;
}
